import React from 'react'
import i18n from 'config/i18n'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { parseBackendDate, getDurationInDaysAndHours, getDurationByDays } from 'services/dates'
import { RegistrationBlock } from 'services/api/registrations'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { BLOCK_TYPE } from 'state/registrations'
import { LocationCell } from 'components/Shared/MuiTable/Cell/LocationCell'
import { HighlightCell } from 'components/Shared/MuiTable/Cell/HighlightCell'
import { OccupationOption, PLATFORM_ENUM } from 'services/api/constants'
import { CampaignState, REQUIREMENT_TYPE_ENUM, CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { PartiposterProfile, SocialMediaAccount } from 'state/submissions'
import { RegistrationResultState } from 'state/registrations'
import { REGISTRATION_STATUS_ENUM } from 'services/api/registrations'
import { theme } from 'theme'
import { getLanguageCode } from 'config/i18n'
import StatusCell from 'components/Shared/MuiTable/Cell/StatusCell'
import { TIMESTAMP_KEY, RegistrationResultTableRows, GridColDefProps } from './constants'
import { CampaignSubmission } from 'components/Shared/Permission/enums'
import { usePermission } from 'hooks/permissions'
import { showDefaultIgPlatform } from 'components/utils/campaign'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'

export const getColumns = (
    status: REGISTRATION_STATUS_ENUM,
    dynamicCols: GridColDef[],
    campaign: CampaignState,
    canManageTag: boolean,
    searchablePlatforms: PLATFORM_ENUM[],
    followerRequirementsPlatform: PLATFORM_ENUM[],
    updateViewedRow?: (id: string) => void,
    handleHighlightRegistration?: (id: string, highlighted: boolean) => void,
): GridColDefProps[] => {
    const canApproveRegistrationAndManageWaitlist = usePermission(
        CampaignSubmission.ApproveRegistrationAndManageWaitList,
    )

    if (!campaign) return []
    const { requirements } = campaign
    const hasRequirements = requirements.length > 0
    const hasLocationRequirement =
        requirements.findIndex(requirement => requirement.type === REQUIREMENT_TYPE_ENUM.LOCATION) > -1
    const hasOccupationRequirement =
        requirements.findIndex(requirement => requirement.type === REQUIREMENT_TYPE_ENUM.OCCUPATION) > -1
    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return searchablePlatforms.includes(platform) || followerRequirementsPlatform.includes(platform)
    }

    const cols: GridColDefProps[] = [
        ...(status === REGISTRATION_STATUS_ENUM.PENDING && canApproveRegistrationAndManageWaitlist
            ? [
                  {
                      field: 'highlight',
                      type: 'string',
                      headerName: i18n.t('submission.table.savePartiposterLabel'),
                      width: 64,
                      renderCell: function Highlight(params: GridRenderCellParams): React.ReactElement {
                          const { uuid, highlighted } = params?.row
                          const handleHighlight = () => {
                              if (handleHighlightRegistration) {
                                  handleHighlightRegistration(uuid, highlighted)
                              }
                          }
                          return (
                              <HighlightCell
                                  {...params}
                                  icon="bookmarkSimple"
                                  onClick={handleHighlight}
                                  isDisabled={campaign.status === CAMPAIGN_STATUS_ENUM.CLOSED}
                              />
                          )
                      },
                      sortable: false,
                  },
              ]
            : []),
        ...(status === REGISTRATION_STATUS_ENUM.PENDING
            ? [
                  {
                      field: 'status',
                      type: 'string',
                      headerName: i18n.t('submission.table.statusLabel'),
                      width: 100,
                      renderCell: (props: GridRenderCellParams) => <StatusCell {...props} />,
                      sortable: false,
                  },
              ]
            : []),

        {
            field: 'profile',
            type: 'string',
            headerName: i18n.t('submission.table.profile'),
            width: 160,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            subFields: [
                {
                    field: 'name',
                    headerName: i18n.t('submission.table.nameLabel'),
                },
                {
                    field: 'gender',
                    headerName: i18n.t('submission.table.genderLabel'),
                },
                {
                    field: 'age',
                    headerName: i18n.t('submission.table.ageLabel'),
                },
                ...(hasRequirements
                    ? [
                          {
                              field: 'meetRequirement',
                              headerName: i18n.t('submission.table.meetRequirementsLabel'),
                          },
                      ]
                    : []),
            ],
        },
        {
            field: 'socialMediaInfo',
            type: 'string',
            headerName: i18n.t('submission.table.socialMediaInfo'),
            width: 180,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams): React.ReactElement => {
                const handleUpdateViewed = () => {
                    if (updateViewedRow) {
                        updateViewedRow(props.row.uuid)
                    }
                }
                return <SocialMediaInfoCell {...props} onClick={handleUpdateViewed} />
            },
            subFields: [
                ...(showDefaultIgPlatform(searchablePlatforms, followerRequirementsPlatform)
                    ? [
                          {
                              field: FIELDS_TYPE.IG_FOLLOWERS,
                              headerName: i18n.t('submission.table.instagramFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.IG_USERNAME,
                              headerName: i18n.t('submission.table.instagramUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.FB)
                    ? [
                          {
                              field: FIELDS_TYPE.FB_FRIENDS,
                              headerName: i18n.t('submission.table.facebookFriendsLabel'),
                          },
                          {
                              field: FIELDS_TYPE.FB_USERNAME,
                              headerName: i18n.t('submission.table.facebookUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                    ? [
                          {
                              field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                              headerName: i18n.t('submission.table.tikTokFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TIKTOK_USERNAME,
                              headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                    ? [
                          {
                              field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                              headerName: i18n.t('submission.table.twitterFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TWITTER_USERNAME,
                              headerName: i18n.t('submission.table.twitterUsernameLabel'),
                          },
                      ]
                    : []),
            ],
        },
        ...(status === REGISTRATION_STATUS_ENUM.AWAITING_RESPONSE
            ? [
                  {
                      field: 'approval_expired_at',
                      type: 'string',
                      headerName: i18n.t('submission.table.registrationExpiredDateLabel'),
                      width: 200,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        ...(hasLocationRequirement
            ? [
                  {
                      field: 'location',
                      headerName: i18n.t('submission.table.locationLabel'),
                      width: 232,
                      renderCell: (props: GridRenderCellParams) => <LocationCell {...props} />,
                      sortable: false,
                  },
              ]
            : []),
        ...(hasOccupationRequirement
            ? [
                  {
                      field: 'occupation',
                      headerName: i18n.t('submission.table.occupationLabel'),
                      width: 172,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),
        ...(status !== REGISTRATION_STATUS_ENUM.WAITLISTED && status !== REGISTRATION_STATUS_ENUM.PENDING
            ? [
                  {
                      field: TIMESTAMP_KEY[status],
                      headerName: i18n.t('submission.table.selectedDateLabel'),
                      width: 192,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
    ]
    return cols.concat(dynamicCols).concat([
        ...(status === REGISTRATION_STATUS_ENUM.JOINED_CAMPAIGN
            ? [
                  {
                      field: 'first_content_submitted_at',
                      headerName: i18n.t('submission.table.startedTaskDateLabel'),
                      width: 192,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        ...(status === REGISTRATION_STATUS_ENUM.PENDING
            ? [
                  {
                      field: TIMESTAMP_KEY[status],
                      headerName: i18n.t('submission.table.joinedDateLabel'),
                      width: 192,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        ...(status !== REGISTRATION_STATUS_ENUM.PENDING
            ? [
                  {
                      field: 'lastReviewedBy',
                      type: 'string',
                      headerName: i18n.t('submission.table.lastReviewedLabel'),
                      minWidth: 180,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
                  {
                      field: 'lastReviewedAt',
                      type: 'string',
                      headerName: i18n.t('submission.table.lastReviewedDateLabel'),
                      minWidth: 180,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        ...(canManageTag
            ? [
                  {
                      field: 'partiposterTags',
                      headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                      width: 240,
                      minWidth: 200,
                      renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                  },
              ]
            : []),
    ])
}

export const parseResultData = (resultData: any, registrationFormBlocksById: Record<string, RegistrationBlock>) => {
    if (!resultData.results || resultData.results.length === 0) return null
    const block = registrationFormBlocksById[resultData.uuid]
    const blockType = block && block.type
    switch (blockType) {
        case BLOCK_TYPE.TEXT_SUBMISSION:
            return { value: resultData.results[0].data.text }
        case BLOCK_TYPE.NUMBER_SUBMISSION:
            return { value: resultData.results[0].data.number }
        case BLOCK_TYPE.URL_SUBMISSION:
            return resultData.results[0].data.url
        case BLOCK_TYPE.PHOTO_SUBMISSION:
            return resultData.results[0].images
        case BLOCK_TYPE.QUESTION:
            return {
                value: resultData.results
                    .map((result: any) => {
                        const target = block.options.find(option => option.uuid === result.option_uuid)
                        if (target && target.is_others) return result.data.option_others
                        return target && target.option
                    })
                    .join(', '),
            }
        default:
            return null
    }
}

export const getRows = (
    status: REGISTRATION_STATUS_ENUM,
    registrations: RegistrationResultState[],
    profileById: Record<string, PartiposterProfile>,
    registrationFormBlocksById: Record<string, RegistrationBlock>,
    campaign: CampaignState,
    occupationOptions: OccupationOption[],
    viewedRows?: string[],
    highlightedRegistrations?: string[],
): RegistrationResultTableRows[] => {
    return registrations.map((registration, index: number) => {
        const profile = profileById[registration.partiposterId]
        const {
            submission_uuid,
            unmet_requirement_types,
            first_content_submitted_at,
            is_expiration_reverted,
            approval_expired_at,
            reviewed_by,
            approved_at,
        } = registration
        const { social_media_accounts: socialMediaAccounts, live_location, work_location, hangout_location } = profile

        const igProfile = socialMediaAccounts.find(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.IG,
        )
        const fbProfile = socialMediaAccounts.find(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.FB,
        )
        const tiktokProfile = socialMediaAccounts.find(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TIKTOK,
        )
        const twitterProfile = socialMediaAccounts.find(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TWITTER,
        )
        const occupation = occupationOptions.find(option => option.id === profile.occupation)
        const timestamp = registration[TIMESTAMP_KEY[status]]
        const locale = getLanguageCode()
        const backendDate = parseBackendDate(timestamp || '', campaign.country, { showTime: true }) || ''
        const resultsData = registration.results_by_block.reduce((acc, curr) => {
            const targetBlock = registrationFormBlocksById[curr.uuid]
            if (!targetBlock) return acc
            return {
                ...acc,
                [`${targetBlock.type}+${curr.uuid}`]: parseResultData(curr, registrationFormBlocksById),
            }
        }, {})
        const expiryDate = approval_expired_at
            ? getDurationInDaysAndHours(approval_expired_at, locale ? locale : 'en', campaign.country)
            : null
        const expiryDays = getDurationByDays(approval_expired_at, campaign.country)
        const isViewed = viewedRows && viewedRows?.includes(registration.uuid)
        const isHighlighted = highlightedRegistrations && highlightedRegistrations?.includes(registration.uuid)
        const hasRequirements = campaign.requirements.length > 0

        return {
            id: registration.uuid,
            uuid: registration.uuid,
            highlighted: isHighlighted,
            approval_expired_at: {
                value: expiryDate ? expiryDate : '',
                color:
                    expiryDays >= 3
                        ? theme.colors.jungleGreen
                        : expiryDays >= 1
                          ? theme.colors.orange
                          : theme.colors.monza,
            },
            profile: {
                unmetRequirement: unmet_requirement_types,
                profile,
                hasRequirements,
                displayExpirationBadge: is_expiration_reverted,
            },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile],
            },
            status: isViewed,
            occupation: { value: occupation?.label },
            location: {
                value: {
                    live: live_location,
                    work: work_location,
                    hangout: hangout_location,
                },
            },
            [TIMESTAMP_KEY[status]]: { value: backendDate },
            first_content_submitted_at: {
                value: first_content_submitted_at
                    ? parseBackendDate(first_content_submitted_at, campaign.country, { showTime: true })
                    : i18n.t('submission.table.notYetStartedTaskContent', 'Not yet'),
                color: first_content_submitted_at ? undefined : theme.colors.silver,
            },
            lastReviewedBy: {
                value: reviewed_by?.name,
            },
            lastReviewedAt: {
                value: approved_at ? parseBackendDate(approved_at, campaign.country, { showTime: true }) : undefined,
            },
            partiposterTags: {
                profileData: profile,
                submissionUuid: submission_uuid,
                campaignUuid: campaign.uuid,
            },
            ...resultsData,
        }
    })
}
